/**
 * Turning minor units and instants into something a person reads.
 *
 * `formatMoney` comes from the kernel and is not re-implemented here. That is
 * not tidiness: `no-float-money` fails the build on `.toFixed(` anywhere, and
 * the reason is that every hand-rolled money formatter this industry has ever
 * written eventually divides by 100 in a float and loses a haléř on a number
 * somebody is going to ask about.
 */

import { czk, formatMoney } from "@gf/kernel/money";

export const money = (minor: bigint, locale = "cs-CZ"): string =>
  formatMoney(czk(minor), locale);

/** A signed amount, with the sign kept — a variance reads wrong without it. */
export function signedMoney(minor: bigint, locale = "cs-CZ"): string {
  const formatted = money(minor < 0n ? -minor : minor, locale);
  if (minor === 0n) return formatted;
  return `${minor > 0n ? "+" : "−"} ${formatted}`;
}

/** A VAT rate in basis points, as a person writes it. */
export function rate(rateBp: number, locale = "cs-CZ"): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 2,
  }).format(rateBp / 10_000);
}

/**
 * A SHARE of a whole — "54,4 %" — always to one decimal.
 *
 * Not `rate()`, which is for VAT and drops trailing zeros: in a column of
 * shares it printed "54,4 %" over "45,59 %", and figures in one column must
 * agree on their precision or they do not line up.
 */
export function share(shareBp: number, locale = "cs-CZ"): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(shareBp / 10_000);
}

const DATE_OPTS: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };

/** A business date — a LABEL the venue already decided, never re-derived. */
export function businessDate(iso: string, locale = "cs-CZ"): string {
  // Parsed as UTC noon, not midnight: a business date is not an instant, and
  // rendering it through a local timezone at midnight shifts it by a day for
  // anybody west of Greenwich.
  return new Intl.DateTimeFormat(locale, DATE_OPTS).format(new Date(`${iso}T12:00:00Z`));
}

export function weekday(iso: string, locale = "cs-CZ"): string {
  return new Intl.DateTimeFormat(locale, { weekday: "short" }).format(
    new Date(`${iso}T12:00:00Z`),
  );
}

/** The clock time of an instant, in the venue's zone. */
export function clockTime(iso: string, timezone: string, locale = "cs-CZ"): string {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone,
  }).format(new Date(iso));
}

/**
 * A duration in seconds, as a kitchen would say it.
 *
 * Minutes and seconds up to an hour, then hours and minutes. "4 500 s" is a
 * number; "1:15" is a service time.
 */
export function duration(seconds: number | null): string {
  if (seconds === null) return "—";
  const whole = Math.round(seconds);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  if (m < 60) return `${String(m)}:${String(s).padStart(2, "0")}`;
  return `${String(Math.floor(m / 60))} h ${String(m % 60)} min`;
}

/**
 * A service time, with its unit. "9:00" in a column beside a clock-time column
 * reads as nine o'clock; "9 min" cannot. Whole minutes are the common case on a
 * kitchen report; the seconds are kept when there are any.
 */
export function serviceTime(seconds: number | null): string {
  if (seconds === null) return "—";
  const whole = Math.round(seconds);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  if (m >= 60) return duration(whole);
  if (m === 0) return `${String(s)} s`;
  return s === 0 ? `${String(m)} min` : `${String(m)} min ${String(s)} s`;
}

/** Today, as the VENUE's trading day rather than the browser's calendar. */
export function venueToday(now: Date, timezone: string, dayStartMinutes: number): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);

  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? "00";
  const date = `${get("year")}-${get("month")}-${get("day")}`;
  const minutes = Number(get("hour")) * 60 + Number(get("minute"));

  // Before the cutover the venue is still trading yesterday — which is the
  // whole reason a business day exists. 01:30 on Saturday belongs to Friday.
  if (minutes >= dayStartMinutes) return date;
  const yesterday = new Date(`${date}T12:00:00Z`);
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  return yesterday.toISOString().slice(0, 10);
}

/** N days before an ISO date, as an ISO date. */
export function daysBefore(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}
